import React from 'react';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Badge,
} from '@tonic/shared/components/ui';
import { DashboardChart } from '@tonic/shared/components/ui';
import { Calendar } from 'lucide-react';

// Mock data for repair analytics
const mockRepairData = {
  repairsByStatus: [
    { name: 'Completed', value: 145 },
    { name: 'In Progress', value: 64 },
    { name: 'Pending', value: 32 },
    { name: 'Cancelled', value: 12 },
  ],
  repairsByType: [
    { name: 'Furniture Assembly', value: 89 },
    { name: 'Upholstery', value: 45 },
    { name: 'Wood Repair', value: 67 },
    { name: 'Hardware Fix', value: 40 },
  ],
  monthlyTrends: [
    { date: '2024-01', repairs: 42, avgTime: 3.2 },
    { date: '2024-02', repairs: 58, avgTime: 2.8 },
    { date: '2024-03', repairs: 75, avgTime: 2.5 },
  ],
  technicianPerformance: [
    { name: 'Mike Wilson', completed: 48, rating: 4.8 },
    { name: 'Sarah Brown', completed: 35, rating: 4.9 },
    { name: 'Tom Davis', completed: 42, rating: 4.7 },
  ],
  customerSatisfaction: [
    { date: '2024-01', satisfaction: 4.5 },
    { date: '2024-02', satisfaction: 4.7 },
    { date: '2024-03', satisfaction: 4.8 },
  ],
  repairTimeDistribution: [
    { range: '0-2 days', count: 45 },
    { range: '3-5 days', count: 78 },
    { range: '6-7 days', count: 34 },
    { range: '8+ days', count: 12 },
  ],
};

const timeRanges = [
  { value: '7d', label: 'Last 7 Days' },
  { value: '30d', label: 'Last 30 Days' },
  { value: '90d', label: 'Last 90 Days' },
  { value: '12m', label: 'Last 12 Months' },
];

export function RepairAnalyticsPage() {
  const [timeRange, setTimeRange] = React.useState('30d');

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Repair Analytics</h1>
          <p className="text-sm text-muted-foreground">
            Overview of repair operations and performance metrics
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Calendar className="w-5 h-5 text-muted-foreground" />
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {timeRanges.map((range) => (
                <SelectItem key={range.value} value={range.value}>
                  {range.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Repairs by Status</CardTitle>
          </CardHeader>
          <CardContent>
            <DashboardChart
              type="pie"
              data={mockRepairData.repairsByStatus}
              dataKey="value"
              xAxisKey="name"
              height={300}
              colors={['#10B981', '#6366F1', '#F59E0B', '#EF4444']}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Repairs by Type</CardTitle>
          </CardHeader>
          <CardContent>
            <DashboardChart
              type="pie"
              data={mockRepairData.repairsByType}
              dataKey="value"
              xAxisKey="name"
              height={300}
              colors={['#6366F1', '#10B981', '#F59E0B', '#3B82F6']}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Monthly Repair Trends</CardTitle>
          </CardHeader>
          <CardContent>
            <DashboardChart
              type="line"
              data={mockRepairData.monthlyTrends}
              dataKey="repairs"
              xAxisKey="date"
              height={300}
              colors={['#6366F1']}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Average Repair Time</CardTitle>
          </CardHeader>
          <CardContent>
            <DashboardChart
              type="line"
              data={mockRepairData.monthlyTrends}
              dataKey="avgTime"
              xAxisKey="date"
              height={300}
              colors={['#10B981']}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Customer Satisfaction</CardTitle>
          </CardHeader>
          <CardContent>
            <DashboardChart
              type="line"
              data={mockRepairData.customerSatisfaction}
              dataKey="satisfaction"
              xAxisKey="date"
              height={300}
              colors={['#F59E0B']}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Repair Time Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <DashboardChart
              type="bar"
              data={mockRepairData.repairTimeDistribution}
              dataKey="count"
              xAxisKey="range"
              height={300}
              colors={['#6366F1']}
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
